import { useEffect, useRef, useState, type ReactNode } from "react";
import { BarChart3, Database, DatabaseZap, Network, Plus, Route } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import ConnectorCatalog from "@/frontend/features/Fde/data/ConnectorCatalog";
import ConnectionList from "@/frontend/features/Fde/data/ConnectionList";
import DataAnalysisPanel from "@/frontend/features/Fde/data/DataAnalysisPanel";
import DataExplorer from "@/frontend/features/Fde/data/DataExplorer";
import DestinationPanel from "@/frontend/features/Fde/data/DestinationPanel";
import SourceSetupDialog from "@/frontend/features/Fde/data/SourceSetupDialog";
import SemanticDataExplorer from "@/frontend/features/Fde/data/SemanticDataExplorer";
import RuntimeProjectLink from "@/frontend/features/DashboardRuntime/RuntimeProjectLink";
import type { ForgeConnector, ForgeDataPayload } from "@/frontend/features/Fde/data/types";

type Tab = "semantic" | "explore" | "dashboards" | "connections";

interface Props {
  data: ForgeDataPayload;
  projectId?: string | null;
  copy: Record<string, string>;
  canManage: boolean;
  isAdmin: boolean;
  workspaceActions?: ReactNode;
}

export default function DataHub({ data, projectId, copy, canManage, isAdmin, workspaceActions }: Props) {
  const visualizations = data.visualizations ?? [];
  const [tab, setTab] = useState<Tab>(() => {
    const requested = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("data_tab");
    if (requested === "semantic" || requested === "explore" || requested === "dashboards" || requested === "connections") return requested;
    return projectId && data.semantic_layer?.object_types.length ? "semantic" : "explore";
  });
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [destinationOpen, setDestinationOpen] = useState(false);
  const [connector, setConnector] = useState<ForgeConnector | null>(null);
  const visualizationSignature = visualizations.map((item) => `${item.path}:${item.provenance.updated_at ?? ""}`).join("|");
  const previousVisualizationSignature = useRef(visualizationSignature);
  const effective = data.destinations.find((item) => item.id === data.effective_destination_id);
  const tabs: Array<{ id: Tab; label: string; icon: typeof Database; count?: number }> = [
    { id: "semantic", label: copy.semantic_layer, icon: Network, count: data.semantic_layer?.object_types.length || 0 },
    { id: "explore", label: copy.explore, icon: Database },
    { id: "dashboards", label: copy.dashboards, icon: BarChart3, count: visualizations.length },
    { id: "connections", label: copy.connections, icon: Route, count: data.connections.length },
  ];

  useEffect(() => {
    if (previousVisualizationSignature.current !== visualizationSignature && visualizations.length > 0) {
      setTab("dashboards");
    }
    previousVisualizationSignature.current = visualizationSignature;
  }, [visualizationSignature, visualizations.length]);

  const selectConnector = (selected: ForgeConnector) => {
    setCatalogOpen(false);
    setConnector(selected);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-border/60 px-4 py-2">
        <div className="flex min-w-0 basis-full flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground sm:flex-1 sm:basis-auto">
          <span className="inline-flex shrink-0 items-center gap-1.5" title={data.stack.sync_online ? copy.forge_online : copy.forge_offline}>
            <span className={cn("size-1.5 rounded-full", data.stack.sync_online ? "bg-emerald-500" : "bg-destructive")} />
            {copy.connections}: {data.summary.healthy_connections}/{data.summary.connections}
          </span>
          <button type="button" onClick={() => setDestinationOpen(true)} className="inline-flex min-w-0 max-w-64 items-center gap-1.5 hover:text-foreground" title={effective?.name || copy.destination_required}>
            <DatabaseZap className="size-3.5 shrink-0" />
            <span className="truncate">{effective?.name || copy.destination_required}</span>
          </button>
          {data.stack.error && <span role="status" className="basis-full text-xs text-destructive [overflow-wrap:anywhere]">{data.stack.error}</span>}
        </div>
        <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">
          <RuntimeProjectLink projectId={projectId} label={copy.runtime_dashboards} />
          {canManage && <Button className="h-8" size="sm" onClick={() => setCatalogOpen(true)}><Plus className="size-4" />{copy.add_source}</Button>}
          {workspaceActions && <div className="flex items-center">{workspaceActions}</div>}
        </div>
      </div>
      <nav className="flex min-h-10 shrink-0 items-center gap-1 overflow-x-auto border-b border-border px-3" aria-label={copy.data_navigation}>
        {tabs.map(({ id, label, icon: Icon, count }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "flex h-10 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 text-sm font-medium transition-colors",
              tab === id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="size-4" />{label}
            {count != null && count > 0 && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums">{count}</span>
            )}
          </button>
        ))}
      </nav>
      <div className="min-h-0 flex-1 overflow-hidden">
        {tab === "semantic" && projectId && <SemanticDataExplorer projectId={projectId} layer={data.semantic_layer || { object_types: [], link_types: [], summary: { object_types: 0, hydrated: 0, properties: 0, mapped_properties: 0, link_types: 0, mapped_link_types: 0 } }} copy={copy} />}
        {tab === "explore" && <DataExplorer projectId={projectId} analytics={data.analytics} semanticLayer={data.semantic_layer} copy={copy} />}
        {tab === "dashboards" && (
          <DataAnalysisPanel projectId={projectId} visualizations={visualizations} copy={copy} />
        )}
        {tab === "connections" && (
          <ConnectionList
            connections={data.connections}
            destinations={data.destinations}
            projectId={projectId}
            copy={copy}
            canManage={canManage && data.stack.sync_online}
            onAdd={() => setCatalogOpen(true)}
          />
        )}
      </div>

      <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
        <DialogContent className="flex h-[min(780px,88dvh)] max-w-6xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b border-border px-5 py-4">
            <DialogTitle>{copy.catalog}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1">
            <ConnectorCatalog
              connectors={data.connectors}
              copy={copy}
              disabled={!canManage || !data.stack.sync_online || data.destinations.length === 0}
              onSelect={selectConnector}
            />
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={destinationOpen} onOpenChange={setDestinationOpen}>
        <DialogContent className="flex max-h-[82dvh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b border-border px-5 py-4">
            <DialogTitle>{copy.destinations}</DialogTitle>
          </DialogHeader>
          <DestinationPanel
            destinations={data.destinations}
            effectiveDestinationId={data.effective_destination_id}
            copy={copy}
            isAdmin={isAdmin}
          />
        </DialogContent>
      </Dialog>
      <SourceSetupDialog
        connector={connector}
        destinations={data.destinations}
        effectiveDestinationId={data.effective_destination_id}
        projectId={projectId}
        copy={copy}
        onClose={() => setConnector(null)}
      />
    </div>
  );
}
